/**
 * O portal em si — o que o trabalhador vê depois de entrar.
 *
 * Quatro blocos, na ordem em que ele pergunta: quando é meu próximo exame, o que eu já fiz,
 * quais treinamentos tenho, e quais afastamentos constam. Tudo em cartão e não em tabela: quem
 * abre isto abre no celular, e tabela de sete colunas não cabe.
 *
 * O CID aparece por extenso. No resto do sistema ele é dado restrito porque quem olha é o
 * empregador; aqui quem olha é o titular do diagnóstico, e esconder dele o próprio CID seria
 * proteger a pessoa dela mesma.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import portalService, { SessaoExpirada } from '../../services/portalService';
import type {
  AfastamentoDoTrabalhador,
  ExameDoTrabalhador,
  IdentidadeDoTrabalhador,
  TreinamentoDoTrabalhador,
} from '../../types/portal';

function dataBr(iso: string | null): string {
  if (!iso) return '—';
  const [ano, mes, dia] = iso.slice(0, 10).split('-');
  return `${dia}/${mes}/${ano}`;
}

interface BlocoProps {
  titulo: string;
  vazio: string;
  children: React.ReactNode;
  temItens: boolean;
}

const Bloco: React.FC<BlocoProps> = ({ titulo, vazio, children, temItens }) => (
  <Paper variant="outlined" sx={{ p: 2.5 }}>
    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
      {titulo}
    </Typography>
    {temItens ? (
      <Stack divider={<Divider flexItem />} spacing={1.5}>
        {children}
      </Stack>
    ) : (
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        {vazio}
      </Typography>
    )}
  </Paper>
);

export const PortalPage: React.FC = () => {
  const { token } = useParams<{ token?: string }>();
  const navegar = useNavigate();

  const [carregando, setCarregando] = useState(true);
  const [expirou, setExpirou] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const [eu, setEu] = useState<IdentidadeDoTrabalhador | null>(null);
  const [agendamentos, setAgendamentos] = useState<ExameDoTrabalhador[]>([]);
  const [exames, setExames] = useState<ExameDoTrabalhador[]>([]);
  const [treinamentos, setTreinamentos] = useState<TreinamentoDoTrabalhador[]>([]);
  const [afastamentos, setAfastamentos] = useState<AfastamentoDoTrabalhador[]>([]);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    try {
      // O token do link é gasto aqui. A partir daí só a sessão vale, e ela não viaja na URL.
      if (token) await portalService.entrar(token);

      const [i, ag, ex, tr, af] = await Promise.all([
        portalService.eu(),
        portalService.agendamentos(),
        portalService.exames(),
        portalService.treinamentos(),
        portalService.afastamentos(),
      ]);
      setEu(i);
      setAgendamentos(ag);
      setExames(ex);
      setTreinamentos(tr);
      setAfastamentos(af);

      // Tira o token da barra de endereços assim que ele deixa de servir: URL vai para o
      // histórico do navegador e para o `Referer` de qualquer link clicado daqui.
      if (token) window.history.replaceState({}, '', '/portal');
    } catch (e) {
      if (e instanceof SessaoExpirada) setExpirou(true);
      else setErro(e instanceof Error ? e.message : 'Não foi possível carregar seus dados.');
    } finally {
      setCarregando(false);
    }
  }, [token]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const sair = useCallback(async () => {
    await portalService.sair();
    navegar('/portal/entrar');
  }, [navegar]);

  if (carregando) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (expirou) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', p: 2 }}>
        <Paper variant="outlined" sx={{ p: 4, maxWidth: 420 }}>
          <Stack spacing={2}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Este link não vale mais
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Cada link abre uma vez só e vale por 20 minutos. Peça um novo com o seu CPF.
            </Typography>
            <Button variant="contained" onClick={() => navegar('/portal/entrar')}>
              Pedir novo link
            </Button>
          </Stack>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: 3 }}>
      <Box sx={{ maxWidth: 760, mx: 'auto', px: 2 }}>
        <Stack spacing={2}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                {eu?.nome ?? 'Portal do Trabalhador'}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {[eu?.empresa, eu?.cargo, eu?.setor].filter(Boolean).join(' · ')}
              </Typography>
            </Box>
            <Button size="small" onClick={() => void sair()}>
              Sair
            </Button>
          </Stack>

          {erro && <Alert severity="error">{erro}</Alert>}

          <Bloco
            titulo="Próximos exames"
            vazio="Você não tem exame marcado no momento."
            temItens={agendamentos.length > 0}
          >
            {agendamentos.map((a) => (
              <Box key={a.id}>
                <Typography sx={{ fontWeight: 600 }}>{a.tipo}</Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {dataBr(a.data)}
                  {a.clinica ? ` · ${a.clinica}` : ''} · {a.situacao}
                </Typography>
              </Box>
            ))}
          </Bloco>

          <Bloco
            titulo="Meus exames"
            vazio="Nenhum exame registrado ainda."
            temItens={exames.length > 0}
          >
            {exames.map((e) => (
              <Box key={e.id}>
                <Typography sx={{ fontWeight: 600 }}>{e.tipo}</Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {dataBr(e.data)}
                  {e.clinica ? ` · ${e.clinica}` : ''} · {e.situacao}
                </Typography>
              </Box>
            ))}
          </Bloco>

          <Bloco
            titulo="Meus treinamentos"
            vazio="Nenhum treinamento registrado ainda."
            temItens={treinamentos.length > 0}
          >
            {treinamentos.map((t) => (
              <Box key={t.id}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Typography sx={{ fontWeight: 600 }}>{t.nome ?? 'Treinamento'}</Typography>
                  {t.vencido && <Chip size="small" color="warning" label="Vencido" />}
                </Stack>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Realizado em {dataBr(t.realizadoEm)}
                  {t.validoAte ? ` · válido até ${dataBr(t.validoAte)}` : ''}
                </Typography>
              </Box>
            ))}
          </Bloco>

          <Bloco
            titulo="Meus afastamentos"
            vazio="Nenhum afastamento registrado."
            temItens={afastamentos.length > 0}
          >
            {afastamentos.map((a) => (
              <Box key={a.id}>
                <Typography sx={{ fontWeight: 600 }}>
                  {dataBr(a.inicio)} a {dataBr(a.fim)} · {a.dias} dia{a.dias > 1 ? 's' : ''}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {a.motivo}
                  {a.cid ? ` · CID ${a.cid}${a.cidDescricao ? ` — ${a.cidDescricao}` : ''}` : ''}
                </Typography>
              </Box>
            ))}
          </Bloco>

          <Typography variant="caption" sx={{ color: 'text.secondary', textAlign: 'center' }}>
            Sua sessão fecha sozinha em 45 minutos. Se algum dado estiver errado, fale com o RH
            da sua empresa.
          </Typography>
        </Stack>
      </Box>
    </Box>
  );
};

export default PortalPage;
